// Device listing without any audio engine, so the page header can check the headset cheaply.

export interface AudioDevice {
  deviceId: string;
  label: string;
}

/** Chrome lists the system default and communications devices under these ids. */
export const DEFAULT_DEVICE_ID = 'default';
const SPECIAL_IDS = new Set([DEFAULT_DEVICE_ID, 'communications', '']);

/** Audio inputs and outputs (labels are empty until microphone permission is granted). */
export async function listAudioDevices(): Promise<{ inputs: AudioDevice[]; outputs: AudioDevice[] }> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const pick = (kind: MediaDeviceKind) =>
    devices.filter((d) => d.kind === kind).map((d) => ({ deviceId: d.deviceId, label: d.label }));
  return { inputs: pick('audioinput'), outputs: pick('audiooutput') };
}

/** Real devices only, without Chrome's "default" and "communications" aliases. */
export function realDevices(list: readonly AudioDevice[]): AudioDevice[] {
  return list.filter((d) => !SPECIAL_IDS.has(d.deviceId));
}

/** "Default - Headphones (Realtek(R) Audio)" → "Headphones (Realtek(R) Audio)". */
export function deviceName(label: string): string {
  return label.replace(/^(default|communications|varsay[ıi]lan|ileti[şs]im)\s*-\s*/i, '');
}

/** The device the system currently uses by default, when the browser says so (Chrome does). */
export function systemDefault(list: readonly AudioDevice[]): AudioDevice | null {
  return list.find((d) => d.deviceId === DEFAULT_DEVICE_ID) ?? null;
}

/**
 * A Bluetooth device, when its name says so. Chrome and Edge add " (Bluetooth)" to the names of
 * Bluetooth inputs and outputs on Windows and macOS (" (Bluetooth LE)" on macOS), in English
 * whatever the system language. Without that, as in Firefox, Windows still names a Bluetooth
 * headset's microphone "… Hands-Free". Elsewhere (Linux, Firefox on macOS) the names are the
 * device's own and only a high delay gives Bluetooth away.
 */
export function isBluetoothName(label: string): boolean {
  return /hands[- ]?free|bluetooth/i.test(label);
}
